import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Article {
  id: string;
  slug: string;
  date: string;
  title: string;
  subtitle?: string;
  summary: string;
  content: string;
  tags: string[];
  type: 'article' | 'newsletter' | 'security';
  mediumUrl?: string;
}

// Parse frontmatter from markdown
const parseFrontmatter = (content: string) => {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { metadata: {}, content };

  const frontmatter = match[1];
  const body = match[2].trim();

  const metadata: Record<string, string> = {};
  frontmatter.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return;

    const key = line.slice(0, colonIndex).trim();
    let value = line.slice(colonIndex + 1).trim();

    // Remove quotes
    value = value.replace(/^["']|["']$/g, '');

    // Handle JSON array format for tags
    if (value.startsWith('[') && value.endsWith(']')) {
      try {
        const arr = JSON.parse(value);
        value = arr.join(', ');
      } catch {
        // Keep as-is if parse fails
      }
    }

    if (key) metadata[key] = value;
  });

  return { metadata, content: body };
};

const INITIAL_NEWSLETTER_COUNT = 3;

const Writing = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [securityArticles, setSecurityArticles] = useState<Article[]>([]);
  const [newsletters, setNewsletters] = useState<Article[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllNewsletters, setShowAllNewsletters] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        // Import all markdown files from content/articles
        const modules = import.meta.glob('/content/articles/*.md', { as: 'raw', eager: true });

        const loadedArticles: Article[] = [];
        const loadedSecurity: Article[] = [];
        const loadedNewsletters: Article[] = [];

        for (const [path, content] of Object.entries(modules)) {
          const filename = path.split('/').pop() || '';
          const { metadata, content: body } = parseFrontmatter(content as string);

          // Extract date from filename (e.g., 2025-11-18-newsletter.md)
          const dateMatch = filename.match(/^(\d{4}-\d{2}-\d{2})/);
          const date = dateMatch ? dateMatch[1] : metadata.date || '2024-01-01';

          const item: Article = {
            id: filename,
            slug: metadata.slug || filename.replace(/\.md$/, ''),
            date: metadata.date || date,
            title: metadata.title || 'Untitled',
            subtitle: metadata.subtitle,
            summary: metadata.summary || metadata.excerpt || '',
            content: body,
            tags: metadata.tags ? metadata.tags.split(',').map((t: string) => t.trim()) : [],
            type: metadata.type === 'article' ? 'article' : metadata.type === 'security' ? 'security' : 'newsletter',
            mediumUrl: metadata.mediumUrl,
          };

          if (item.type === 'article') {
            loadedArticles.push(item);
          } else if (item.type === 'security') {
            loadedSecurity.push(item);
          } else {
            loadedNewsletters.push(item);
          }
        }

        // Sort by date descending (newest first)
        loadedArticles.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        loadedSecurity.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        loadedNewsletters.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        setArticles(loadedArticles);
        setSecurityArticles(loadedSecurity);
        setNewsletters(loadedNewsletters);
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  // Find article by slug across all lists
  const findArticleBySlug = (slug: string): Article | undefined => {
    return [...articles, ...securityArticles, ...newsletters].find(a => a.slug === slug);
  };

  // Deep-link: on load, check hash for #article/slug
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    const match = hash.match(/^#article\/(.+)$/);
    if (match) {
      const article = findArticleBySlug(match[1]);
      if (article) {
        setExpandedId(article.id);
        // Scroll to writing section after a short delay for render
        setTimeout(() => {
          document.getElementById('writing')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }

    const onHashChange = () => {
      const h = window.location.hash;
      const m = h.match(/^#article\/(.+)$/);
      if (m) {
        const a = findArticleBySlug(m[1]);
        if (a) {
          setExpandedId(a.id);
          document.getElementById('writing')?.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (!h || h === '#' || h === '#writing') {
        // Don't collapse if navigating to another section
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, articles, securityArticles, newsletters]);

  const toggleExpand = (id: string) => {
    const isCollapsing = expandedId === id;
    setExpandedId(isCollapsing ? null : id);

    // Update URL hash for deep linking
    if (!isCollapsing) {
      const article = [...articles, ...securityArticles, ...newsletters].find(a => a.id === id);
      if (article) {
        window.history.replaceState(null, '', `#article/${article.slug}`);
      }
    } else {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const displayedNewsletters = showAllNewsletters ? newsletters : newsletters.slice(0, INITIAL_NEWSLETTER_COUNT);
  const hasMoreNewsletters = newsletters.length > INITIAL_NEWSLETTER_COUNT;

  const renderArticleCard = (article: Article, isOriginal: boolean = false, isSecurity: boolean = false) => {
    const accentColor = isSecurity ? 'neural-orange' : isOriginal ? 'quantum-green' : 'quantum-purple';
    const hoverBorder = isSecurity ? 'hover:border-neural-orange' : isOriginal ? 'hover:border-quantum-purple' : 'hover:border-quantum-blue';
    const chevronColor = isSecurity ? 'text-neural-orange' : isOriginal ? 'text-quantum-purple' : 'text-quantum-blue';

    return (
    <div
      key={article.id}
      className={`glass rounded-xl overflow-hidden transition-all duration-300 ${hoverBorder}`}
    >
      {/* Article Header - Always Visible */}
      <button
        onClick={() => toggleExpand(article.id)}
        className="w-full p-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className={`text-xs font-mono text-${accentColor}`}>
              {formatDate(article.date)}
            </span>
            {isOriginal && article.mediumUrl && (
              <a
                href={article.mediumUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs px-2 py-0.5 bg-quantum-purple/10 text-quantum-purple rounded hover:bg-quantum-purple/20 transition-colors"
              >
                View on Medium
              </a>
            )}
            <div className="flex gap-2 flex-wrap">
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className={`text-xs px-2 py-0.5 rounded ${
                    isSecurity
                      ? 'bg-neural-orange/10 text-neural-orange'
                      : isOriginal
                        ? 'bg-quantum-green/10 text-quantum-green'
                        : 'bg-quantum-blue/10 text-quantum-blue'
                  }`}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <h3 className="text-lg font-heading font-semibold text-text-primary">
            {article.title}
          </h3>
          {article.subtitle && (
            <p className={`text-sm mt-0.5 italic ${isSecurity ? 'text-neural-orange' : 'text-quantum-blue'}`}>
              {article.subtitle}
            </p>
          )}
          {article.summary && (
            <p className="text-sm text-text-secondary mt-1">
              {article.summary}
            </p>
          )}
        </div>
        <svg
          className={`w-5 h-5 ml-4 flex-shrink-0 transition-transform duration-300 ${chevronColor} ${expandedId === article.id ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Expanded Content */}
      {expandedId === article.id && (
        <div className="px-4 pb-4 border-t border-glass-border">
          <div className="flex justify-end pt-3 pb-1">
            <button
              onClick={() => {
                const url = `${window.location.origin}${window.location.pathname}#article/${article.slug}`;
                navigator.clipboard.writeText(url);
                setCopiedSlug(article.slug);
                setTimeout(() => setCopiedSlug(null), 2000);
              }}
              className={`text-xs px-3 py-1 rounded transition-colors ${
                copiedSlug === article.slug
                  ? 'bg-quantum-green/20 text-quantum-green'
                  : `bg-${accentColor}/10 text-${accentColor} hover:bg-${accentColor}/20`
              }`}
            >
              {copiedSlug === article.slug ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
          <div className={`prose prose-invert prose-sm max-w-none
                          prose-headings:font-heading
                          prose-h1:text-xl prose-h2:text-lg prose-h3:text-base
                          prose-p:text-text-secondary prose-p:leading-relaxed
                          prose-a:no-underline hover:prose-a:underline
                          prose-li:text-text-secondary
                          prose-hr:border-glass-border
                          prose-img:rounded-lg prose-img:my-4
                          prose-table:border-collapse prose-table:w-full prose-table:text-sm
                          prose-th:border prose-th:border-glass-border prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:bg-white/5
                          prose-td:border prose-td:border-glass-border prose-td:px-3 prose-td:py-2
                          ${isSecurity
                            ? 'prose-headings:text-neural-orange prose-strong:text-neural-orange prose-a:text-neural-orange prose-code:text-neural-orange prose-th:text-neural-orange'
                            : 'prose-headings:text-quantum-blue prose-strong:text-quantum-purple prose-a:text-quantum-blue prose-th:text-quantum-blue'
                          }`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
  };

  return (
    <section id="writing" className="section-padding bg-bg-darker">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-6 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-2 md:mb-4">
            Technical <span className="gradient-text">Writing</span>
          </h2>
          <p className="text-sm md:text-lg text-text-secondary max-w-2xl mx-auto">
            Original articles, security research, and AI-generated research digests
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-8 text-text-secondary">Loading content...</div>
          ) : (
            <>
              {/* Original Articles Section */}
              {articles.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-2xl">✍️</div>
                    <h3 className="text-xl font-heading font-semibold text-quantum-green">
                      Discourse
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {articles.map((article) => renderArticleCard(article, true))}
                  </div>
                </div>
              )}

              {/* Security Research Section */}
              {securityArticles.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-2xl">🔬</div>
                    <h3 className="text-xl font-heading font-semibold text-neural-orange">
                      Security Research
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {securityArticles.map((article) => renderArticleCard(article, false, true))}
                  </div>
                </div>
              )}

              {/* AI-Generated Newsletters Section */}
              {newsletters.length > 0 && (
                <div>
                  {/* About the Newsletters */}
                  <div className="glass p-4 md:p-6 rounded-xl mb-6">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="text-2xl md:text-3xl">📰</div>
                      <div>
                        <h3 className="text-base md:text-lg font-heading font-semibold mb-1 md:mb-2 text-quantum-blue">
                          arXiv Weekly Digest
                        </h3>
                        <p className="text-text-secondary text-xs md:text-sm leading-relaxed mb-2 md:mb-3">
                          These newsletters are <span className="text-quantum-purple font-medium">AI-generated</span> from
                          my daily scrapes of newly published papers on{' '}
                          <a
                            href="https://arxiv.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-quantum-blue hover:underline"
                          >
                            arxiv.org
                          </a>.
                          <span className="hidden md:inline"> As an avid reader of arXiv, I built this tool to compile a weekly newsletter
                          highlighting papers I find interesting in AI/ML, quantum computing, and related fields.</span>
                        </p>
                        <p className="text-text-secondary text-xs md:text-sm">
                          <span className="text-quantum-green">Interested in AI-generated content?</span>{' '}
                          <a
                            href="#contact"
                            className="text-quantum-blue hover:underline font-medium"
                          >
                            Let's talk
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Newsletter List */}
                  <div className="space-y-4">
                    {displayedNewsletters.map((newsletter) => renderArticleCard(newsletter, false))}

                    {/* Show More/Less Toggle */}
                    {hasMoreNewsletters && (
                      <div className="text-center mt-4">
                        <button
                          onClick={() => setShowAllNewsletters(!showAllNewsletters)}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-quantum-blue hover:text-quantum-green transition-colors duration-200"
                        >
                          <span>{showAllNewsletters ? 'Show Less' : `Show More (${newsletters.length - INITIAL_NEWSLETTER_COUNT} more)`}</span>
                          <svg
                            className={`w-4 h-4 transition-transform duration-300 ${showAllNewsletters ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {articles.length === 0 && securityArticles.length === 0 && newsletters.length === 0 && (
                <div className="glass p-8 rounded-xl text-center">
                  <p className="text-text-secondary">No content yet. Check back soon!</p>
                </div>
              )}
            </>
          )}

          {/* Topics Covered - Hidden on mobile */}
          <div className="hidden md:block mt-12">
            <h3 className="text-xl font-heading font-semibold mb-6 text-center text-text-secondary">
              Topics I Cover
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                {
                  icon: '🤖',
                  topic: 'AI/ML',
                  description: 'Transformers, RL, and production ML systems',
                },
                {
                  icon: '⚛️',
                  topic: 'Quantum Computing',
                  description: 'Algorithms, hardware, and hybrid approaches',
                },
                {
                  icon: '🔒',
                  topic: 'Security',
                  description: 'Malware analysis, incident response, and threat research',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="glass p-4 rounded-xl text-center hover:border-quantum-purple transition-all duration-300"
                >
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <h4 className="text-sm font-heading font-semibold mb-1 text-quantum-blue">
                    {item.topic}
                  </h4>
                  <p className="text-xs text-text-secondary">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Writing;
